import { computed, reactive } from "vue";
import {
  addPayPlanProposalApprover,
  deletePayPlanProposal,
  executePayPlanProposal,
  getListofEmployeesV2,
  getPayPlanContext,
  getPayPlanVersionDetailV2,
  reconcilePayPlanProposal,
  removePayPlanProposalApprover,
  resetPayPlanProposalToDraft,
  submitPayPlanProposal,
  voidPayPlanProposal,
} from "@/api/employeeAPI";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { resolveReadinessEnabled } from "@components/utils/useReadinessEnabled";

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/** Normalize the proposal status to the drawer's own vocabulary. */
export function normalizeProposalStatus(status) {
  // Accepts the API enum (DRAFT / PENDING / APPROVED…), the page row's display
  // label (Draft / "Pending approval" / Approved / Rejected) or lowercase.
  switch (String(status || "").trim()) {
    case "DRAFT":
    case "Draft":
    case "draft":
      return "draft";
    case "PENDING":
    case "Pending approval":
    case "pending":
      return "pending";
    case "APPROVED":
    case "Approved":
    case "approved":
      return "approved";
    case "PENDING_EXECUTION":
    case "Pending execution":
    case "pending_execution":
      return "pending-execution";
    case "RECONCILED":
    case "Reconciled":
    case "reconciled":
      return "reconciled";
    case "REJECTED":
    case "Rejected":
    case "rejected":
      return "rejected";
    case "CANCELED":
    case "Canceled":
    case "canceled":
    case "VOIDED":
    case "Voided":
    case "voided":
      return "voided";
    default:
      return "draft";
  }
}

/** Extract a human-readable message from an axios error (409 inline errors). */
export function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.details === "string" && data.details.trim()) return data.details;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  const first = data && Object.values(data).find((value) => value != null);
  if (Array.isArray(first) && first[0]) return String(first[0]);
  if (typeof first === "string" && first.trim()) return first;
  return fallback;
}

/**
 * Extract the resulting pay-plan code from a reconcile response (Rule B-12).
 * The action answers with `PayPlanVersionV2Serializer` (ER-12107), which carries
 * the committed row's `version_code` (PP-4.V1) / `plan_code` (PP-4) / `code`.
 * The older `resulting_version*` shapes are still honoured.
 */
export function resolveResultingPlanCode(data) {
  if (!data) return null;
  if (typeof data.resulting_version_code === "string" && data.resulting_version_code.trim()) {
    return data.resulting_version_code;
  }
  if (typeof data.resulting_version === "string" && data.resulting_version.trim()) {
    return data.resulting_version;
  }
  if (data.resulting_version && typeof data.resulting_version === "object") {
    return data.resulting_version.code || data.resulting_version.plan_code || null;
  }
  for (const key of ["version_code", "plan_code", "code"]) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}

/** ECL approval-status vocabulary (ProposedPayPlanDrawer's `approvalStatus`). */
const ECL_APPROVAL_STATUS = {
  draft: "draft",
  pending: "pending-approval",
  approved: "approved",
  "pending-execution": "pending-execution",
  rejected: "rejected",
  reconciled: "approved",
  voided: "rejected",
};

/**
 * Wiring for the ProposedPayPlanDrawer (ER-12109).
 *
 * Owns the drawer's open/close lifecycle, proposal + context loading, and every
 * workflow action, all gated behind the Fair Pay MVP flag (idea-MDP-429). 409
 * responses surface inline with the server's own message; other failures fall
 * back to the proxy's notification service.
 */
export function useProposalDrawer({ proxy, fetchRows } = {}) {
  const state = reactive({
    open: false,
    loading: false,
    submitting: false,
    error: null,
    accountId: null,
    employeeId: null,
    proposal: null,
    // Approval lifecycle of the proposal as reported by the page's row
    // (`approvalStatus`: Draft / Pending approval / Approved / Rejected).
    // The version-detail read does not carry it — see the [BE] ticket.
    approvalStatus: null,
    context: null,
    mutationEnabled: false,
    // Dialogs
    reconcileOpen: false,
    voidOpen: false,
    resetOpen: false,
    deleteConfirmOpen: false,
    payrollVerified: false,
    notifyEmployee: true,
    notifyApprovers: false,
    voidReason: "",
    // Rule B-7a: approver management is only available while the proposal is DRAFT.
    approverItems: [],
    approverPickerOpen: false,
  });

  const proposalStatus = computed(() =>
    // Prefer any approval status the read returns, then the status the page
    // handed us with the row (the only source today), then the version
    // lifecycle (`status`: PROPOSED/IN_FORCE…). Without an approval status the
    // drawer must NOT assume draft, or it offers actions the API rejects.
    normalizeProposalStatus(
      state.proposal?.approval_status ||
        state.approvalStatus ||
        state.proposal?.status
    )
  );

  /** Rule A-40: the payload's `readiness_enabled` is authoritative; the version detail is read first. */
  const readinessEnabled = computed(() =>
    resolveReadinessEnabled(
      typeof state.proposal?.readiness_enabled === "boolean" ? state.proposal : state.context
    )
  );

  /**
   * Map the version detail + context onto the ECL `ProposedPayPlanDrawer`
   * props (mirrors the Lapsed drawer's `mapVersionDetail`).
   */
  const drawerProps = computed(() => {
    const detail = state.proposal;
    if (!detail) return null;
    const fairPay = detail.fair_pay_at_effective_date || null;
    const range = fairPay?.pay_range_version || null;
    const band = state.context?.pay_range_1fte || null;
    return {
      planType: detail.plan_code ? "change-existing-plan" : "new-plan",
      approvalStatus: ECL_APPROVAL_STATUS[proposalStatus.value] || "draft",
      fteAdjusted: Boolean(
        state.context?.fte_adjustment_applied ?? fairPay?.fte_adjustment_applied
      ),
      readinessEnabled: readinessEnabled.value,
      rangePenetration: detail.range_penetration || state.context?.range_penetration || null,
      proposal: {
        employee: detail.employee_name || "",
        employee_title: detail.employee_title || "",
        avatar_url: detail.avatar_url || null,
        state: detail.state || null,
        effective_from: detail.effective_from || null,
        effective_to: detail.effective_to || null,
        country: detail.country || null,
        currency: detail.currency || null,
        amount: detail.amount,
        pay_basis: detail.pay_basis || null,
        fte: detail.allocation_fte ?? null,
        terms_locked: detail.terms_locked ?? false,
        version_code: detail.version_code || "",
      },
      payRange1Fte: {
        min: band?.min ?? range?.anchors?.["0"] ?? null,
        max: band?.max ?? range?.anchors?.["150"] ?? null,
        readiness:
          state.context?.current_readiness_pct ?? fairPay?.readiness_pct ?? null,
        asOfDate: fairPay?.readiness_as_of || null,
        code: range?.code || null,
        level: range?.level || null,
        location: range?.location?.display_label || range?.location?.name || null,
        position: fairPay?.position || null,
        anchors: range?.anchors || null,
      },
      payRangeAdjusted:
        state.context?.pay_range_adjusted || fairPay?.pay_range_adjusted || null,
      // ER-12345: the detail's approvers already match the ECL row shape
      // ({ id, name, role, status, default }); `id` is the Access id, so expose
      // it as `accessId` for remove-approver.
      approvers: (detail.approvers || []).map((approver) => ({
        ...approver,
        accessId: approver.accessId ?? approver.id,
        status: String(approver.status || "draft").toLowerCase(),
      })),
      currentPlan: null,
    };
  });

  function notify(type, message) {
    const notifier = proxy?.$notifications?.[type];
    if (typeof notifier === "function") {
      notifier(message);
    } else if (type === "error") {
      state.error = message;
    }
  }

  function setInlineError(error, fallback) {
    state.error = apiErrorMessage(error, fallback);
  }

  async function ensureMutationEnabled() {
    try {
      state.mutationEnabled =
        (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
    } catch {
      state.mutationEnabled = false;
    }
    return state.mutationEnabled;
  }

  function effectiveDateFrom(proposal) {
    const value =
      proposal?.effective_date || proposal?.effectiveDate || proposal?.effective_from;
    if (typeof value === "string" && value.trim()) return value.slice(0, 10);
    return null;
  }

  function mapEmployeeToApprover(employee) {
    const first = employee?.first_name || "";
    const last = employee?.last_name || "";
    const name = employee?.name || `${first} ${last}`.trim();
    // The detail's `approvers` (ER-12345) key on the Access id
    // (`task.assignee_id`), so an optimistically added row must key on it too —
    // otherwise it would not dedupe against the next read.
    const accessId = employee?.access?.id ?? null;
    return {
      id: accessId ?? employee.id,
      accessId,
      employeeId: employee.id,
      name,
      initials: `${first[0] || ""}${last[0] || ""}`.toUpperCase() || undefined,
      // The ECL renders `role`/`status`; a freshly added approver is a draft
      // task until the proposal is submitted.
      role: employee?.position?.name || employee?.job_title || "Admin-added approver",
      roleLabel: "Admin-added approver",
      status: "draft",
    };
  }

  async function loadApproverItems() {
    if (!state.accountId) return;
    try {
      const response = await getListofEmployeesV2({
        account_id: state.accountId,
        queryParamsObj: {
          limit: 50,
          offset: 0,
          // Approvers must be able to sign in — only employees with an
          // operational access can be named (add-approver rejects others).
          has_access: true,
        },
      });
      const existing = new Set(
        (state.proposal?.approvers || []).map((approver) => String(approver.id))
      );
      state.approverItems = (response?.data?.results || [])
        .filter((employee) => {
          const accessId = employee?.access?.id;
          return accessId == null || !existing.has(String(accessId));
        })
        .map(mapEmployeeToApprover);
    } catch {
      state.approverItems = [];
    }
  }

  function updateEffectiveFrom(date) {
    // Draft proposals may move their effective date; the FTE-adjusted context is
    // re-fetched against the new date (Rule B-20). Persisting the date itself is
    // a separate PATCH owned by ER-12107.
    state.proposal = {
      ...(state.proposal || {}),
      effective_from: date,
      effective_date: date,
    };
    return loadContext(date);
  }

  async function loadContext(effectiveDate) {
    if (!state.accountId || !state.employeeId) return null;
    try {
      const response = await getPayPlanContext(state.accountId, state.employeeId, {
        effective_date: effectiveDate,
      });
      state.context = response?.data || null;
      return state.context;
    } catch {
      state.context = null;
      return null;
    }
  }

  /**
   * The version detail (ER-12115) is the READ surface for a proposal — the
   * `pay-plan-proposals/` route is create/actions only (a GET there is 405).
   * Map its field names onto the drawer's proposal shape.
   */
  function mapVersionToProposal(data) {
    if (!data) return null;
    return {
      ...data,
      code: data.version_code ?? data.code ?? "",
      effective_date: data.effective_from ?? data.effective_date ?? null,
      approvers: data.approvers || [],
    };
  }

  async function loadProposal(versionId) {
    state.loading = true;
    state.error = null;
    try {
      const response = await getPayPlanVersionDetailV2(state.accountId, versionId);
      state.proposal = mapVersionToProposal(response?.data);
      state.employeeId = state.proposal?.employee || state.employeeId;
      await loadContext(effectiveDateFrom(state.proposal));
    } catch (error) {
      state.proposal = null;
      setInlineError(error, "Could not load the proposal");
    } finally {
      state.loading = false;
    }
  }

  function resetDialogs() {
    state.reconcileOpen = false;
    state.voidOpen = false;
    state.resetOpen = false;
    state.deleteConfirmOpen = false;
    state.payrollVerified = false;
    state.notifyEmployee = true;
    state.notifyApprovers = false;
    state.voidReason = "";
    state.approverPickerOpen = false;
    state.approverItems = [];
  }

  function open({ accountId, employeeId, proposalId, proposal, approvalStatus } = {}) {
    state.open = true;
    state.error = null;
    state.accountId = accountId || getAccountId();
    state.employeeId = employeeId || null;
    state.proposal = proposal || null;
    state.approvalStatus = approvalStatus || proposal?.approvalStatus || null;
    resetDialogs();
    ensureMutationEnabled();
    if (proposalId != null) {
      loadProposal(proposalId);
    } else if (state.proposal) {
      loadContext(effectiveDateFrom(state.proposal));
    }
    loadApproverItems();
  }

  function close() {
    state.open = false;
    state.error = null;
    state.proposal = null;
    state.approvalStatus = null;
    state.context = null;
    resetDialogs();
  }

  async function refresh() {
    if (fetchRows) await fetchRows();
  }

  async function runAction(action, { message, fallback }) {
    if (!state.mutationEnabled && !(await ensureMutationEnabled())) {
      notify("error", "Fair Pay proposals are not available");
      return null;
    }
    state.submitting = true;
    state.error = null;
    try {
      const response = await action();
      if (message) notify("success", message);
      return response?.data ?? null;
    } catch (error) {
      if (error?.response?.status === 409) {
        setInlineError(error, fallback);
      } else {
        setInlineError(error, fallback);
        notify("error", apiErrorMessage(error, fallback));
      }
      return null;
    } finally {
      state.submitting = false;
    }
  }

  function proposalId() {
    return state.proposal?.id ?? null;
  }

  async function submit() {
    const data = await runAction(
      () => submitPayPlanProposal(state.accountId, proposalId()),
      { message: "Proposal submitted for approval", fallback: "Could not submit the proposal" }
    );
    if (data) {
      await loadProposal(proposalId());
      await refresh();
    }
  }

  async function execute() {
    const data = await runAction(
      () => executePayPlanProposal(state.accountId, proposalId()),
      { message: "Proposal executed", fallback: "Could not execute the proposal" }
    );
    if (data) {
      await loadProposal(proposalId());
      await refresh();
    }
  }

  async function resetToDraft() {
    const data = await runAction(
      () =>
        resetPayPlanProposalToDraft(state.accountId, proposalId(), {
          notify_approvers: state.notifyApprovers,
        }),
      { message: "Proposal reset to draft", fallback: "Could not reset the proposal" }
    );
    if (data) {
      state.resetOpen = false;
      await loadProposal(proposalId());
      await refresh();
    }
  }

  async function reconcile() {
    // Rule B-12: the Commit confirm is gated on the payroll-verification checkbox.
    if (!state.payrollVerified) {
      notify("error", "Verify these terms against payroll before committing");
      return null;
    }
    const data = await runAction(
      () =>
        reconcilePayPlanProposal(state.accountId, proposalId(), {
          payroll_verified: state.payrollVerified,
          notify_employee: state.notifyEmployee,
        }),
      { fallback: "Could not commit the pay plan" }
    );
    if (data) {
      const code = resolveResultingPlanCode(data);
      notify("success", code ? `Pay plan ${code} committed` : "Pay plan committed");
      state.reconcileOpen = false;
      await loadProposal(proposalId());
      await refresh();
    }
  }

  async function deleteProposal() {
    const ok = await runAction(
      () => deletePayPlanProposal(state.accountId, proposalId()),
      { message: "Proposal deleted", fallback: "Could not delete the proposal" }
    );
    if (ok !== null) {
      close();
      await refresh();
    }
  }

  async function voidProposal() {
    const data = await runAction(
      () => voidPayPlanProposal(state.accountId, proposalId(), { reason: state.voidReason }),
      { message: "Proposal voided", fallback: "Could not void the proposal" }
    );
    if (data) {
      state.voidOpen = false;
      await loadProposal(proposalId());
      await refresh();
    }
  }

  // Rule B-7a: approvers may be added or removed freely while DRAFT (ER-12107).
  async function addApprover(employee) {
    if (!employee?.id) return null;
    const data = await runAction(
      () => addPayPlanProposalApprover(state.accountId, proposalId(), { employee_id: employee.id }),
      { message: "Approver added", fallback: "Could not add the approver" }
    );
    if (data) {
      const approvers = state.proposal?.approvers || [];
      state.proposal = {
        ...(state.proposal || {}),
        approvers: [...approvers, mapEmployeeToApprover(employee)],
      };
      state.approverPickerOpen = false;
      await refresh();
    }
    return data;
  }

  /**
   * Remove an approver. Accepts the row object from the drawer (which carries
   * `accessId` for detail-loaded rows and `employeeId` for freshly picked ones)
   * or a bare id.
   */
  async function removeApprover(approver) {
    const target =
      approver && typeof approver === "object"
        ? approver
        : // Backwards compatible: a bare id is treated as an employee id.
          { id: approver, employeeId: approver };
    if (target.id == null && target.accessId == null && target.employeeId == null) {
      return null;
    }
    const payload = {};
    if (target.accessId != null) payload.access_id = target.accessId;
    if (target.employeeId != null) payload.employee_id = target.employeeId;
    const data = await runAction(
      () => removePayPlanProposalApprover(state.accountId, proposalId(), payload),
      { message: "Approver removed", fallback: "Could not remove the approver" }
    );
    if (data) {
      state.proposal = {
        ...(state.proposal || {}),
        approvers: (state.proposal?.approvers || []).filter(
          (row) => row.id !== target.id
        ),
      };
      await refresh();
    }
    return data;
  }

  return {
    state,
    proposalStatus,
    readinessEnabled,
    drawerProps,
    open,
    close,
    loadContext,
    loadApproverItems,
    updateEffectiveFrom,
    addApprover,
    removeApprover,
    submit,
    execute,
    resetToDraft,
    reconcile,
    deleteProposal,
    voidProposal,
  };
}
